import React, { useState, useEffect } from "react";
import { Modal, Button, FormControl, InputGroup, Row, Col, Spinner, Alert } from "react-bootstrap";
import { ApiHelper, ToastHelper } from "../../helpers";


interface Props {
  onClose: () => void;
  onSelect: (url: string) => void;
  contentType: string;
  contentId: number;
  questionId: number;
}

interface CanvaDesign {
  id: string;
  title: string;
  thumbnail: { url: string; width: number; height: number };
  created_at: number;
  updated_at: number;
}

export const CanvaModal: React.FC<Props> = (props) => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [designs, setDesigns] = useState<CanvaDesign[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<"png" | "mp4">("png");

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      const result = await ApiHelper.get<{ connected: boolean }>("/canva/status");
      setIsConnected(result?.connected ?? false);
      if (result?.connected) {
        loadDesigns();
      }
    } catch (err) {
      setIsConnected(false);
    }
  };

  const handleConnect = async () => {
    try {
      const result = await ApiHelper.get<{ url: string }>("/canva/auth-url");
      if (result?.url) {
        const popup = window.open(result.url, "canva-auth", "width=600,height=700");

        const handleMessage = async (event: MessageEvent) => {
          if (event.data?.type === "canva-callback") {
            window.removeEventListener("message", handleMessage);
            popup?.close();

            if (event.data.code) {
              try {
                await ApiHelper.post("/canva/callback", {
                  code: event.data.code,
                  state: event.data.state
                });
                setIsConnected(true);
                loadDesigns();
                ToastHelper.success("Connected to Canva!");
              } catch (err) {
                ToastHelper.error("Failed to connect to Canva");
              }
            } else if (event.data.error) {
              ToastHelper.error("Canva authorization failed");
            }
          }
        };

        window.addEventListener("message", handleMessage);
      }
    } catch (err) {
      ToastHelper.error("Failed to get Canva authorization URL");
    }
  };

  const loadDesigns = async () => {
    setLoading(true);
    try {
      const query = searchText.trim() || undefined;
      const url = query ? `/canva/designs?query=${encodeURIComponent(query)}` : "/canva/designs";
      const result = await ApiHelper.get<{ designs: CanvaDesign[]; error?: string }>(url);

      if (result?.error === "not_connected") {
        setIsConnected(false);
      } else {
        setDesigns(result?.designs || []);
      }
    } catch (err) {
      ToastHelper.error("Failed to load Canva designs");
    }
    setLoading(false);
  };

  const handleExport = async (design: CanvaDesign) => {
    setExporting(design.id);
    try {
      const result = await ApiHelper.post<{ url?: string; error?: string }>("/canva/export", {
        designId: design.id,
        contentType: props.contentType,
        contentId: props.contentId,
        questionId: props.questionId,
        exportFormat
      });

      if (result?.url) {
        props.onSelect(result.url);
        props.onClose();
        ToastHelper.success("Design exported successfully!");
      } else {
        ToastHelper.error(result?.error || "Export failed");
      }
    } catch (err) {
      ToastHelper.error("Failed to export design");
    }
    setExporting(null);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Canva account?")) return;
    try {
      await ApiHelper.delete("/canva/disconnect");
      setIsConnected(false);
      setDesigns([]);
      ToastHelper.success("Canva account disconnected");
    } catch (err) {
      ToastHelper.error("Failed to disconnect Canva account");
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadDesigns();
  };

  const renderContent = () => {
    if (isConnected === null) {
      return (
        <div className="text-center p-4">
          <Spinner animation="border" />
          <p className="mt-2">Checking Canva connection...</p>
        </div>
      );
    }

    if (!isConnected) {
      return (
        <div className="text-center" style={{ padding: "48px 24px" }}>
          <img src="/images/canva.png" alt="Canva" style={{ width: 64, height: 64, marginBottom: 24 }} />
          <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Connect Your Canva Account</h4>
          <p style={{ color: "#6c757d", marginBottom: 24, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>
            Link your Canva account to import designs directly into SignPresenter. This is a one-time setup.
          </p>
          <Button
            onClick={handleConnect}
            variant="light"
            style={{
              backgroundColor: "#FFFFFF",
              borderColor: "#E0E0E0",
              borderWidth: 1,
              borderStyle: "solid",
              color: "#000000",
              padding: "8px 16px",
              fontSize: "16px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              whiteSpace: "nowrap"
            }}
          >
            <img src="/images/canva.png" alt="Canva" style={{ width: 28, height: 28, marginRight: 12 }} />
            Connect to Canva
          </Button>
        </div>
      );
    }

    return (
      <>
        <form onSubmit={handleSearch} className="mb-3">
          <InputGroup>
            <FormControl
              placeholder="Search your designs..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button variant="primary" type="submit">
              Search
            </Button>
          </InputGroup>
        </form>

        <div className="mb-3 d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: 14 }}>Export as:</span>
          <Button size="sm" variant={exportFormat === "png" ? "primary" : "outline-secondary"} onClick={() => setExportFormat("png")}>PNG Image</Button>
          <Button size="sm" variant={exportFormat === "mp4" ? "primary" : "outline-secondary"} onClick={() => setExportFormat("mp4")}>MP4 Video</Button>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading designs...</p>
          </div>
        ) : designs.length === 0 ? (
          <p className="text-muted text-center">No designs found. Create some designs in Canva first!</p>
        ) : (
          <Row>
            {designs.map((design) => (
              <Col md={3} sm={4} xs={6} key={design.id} className="mb-3">
                <div
                  className="canva-design-card"
                  style={{
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    overflow: "hidden",
                    cursor: exporting ? "not-allowed" : "pointer",
                    opacity: exporting && exporting !== design.id ? 0.5 : 1
                  }}
                  onClick={() => !exporting && handleExport(design)}
                >
                  <img
                    src={design.thumbnail?.url}
                    alt={design.title}
                    style={{ width: "100%", height: 120, objectFit: "cover", backgroundColor: "#f0f0f0" }}
                  />
                  <div className="p-2">
                    <small className="text-truncate d-block" title={design.title}>{design.title || "Untitled"}</small>
                    {exporting === design.id && (
                      <small className="text-primary">
                        <Spinner animation="border" size="sm" className="me-1" /> Exporting...
                      </small>
                    )}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </>
    );
  };

  return (
    <Modal show={true} onHide={props.onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Select Canva Design</Modal.Title>
        {isConnected && (
          <Button variant="link" size="sm" onClick={handleDisconnect}
            style={{ fontSize: 12, color: "#dc3545", marginLeft: "auto", marginRight: 8 }}>
            Disconnect
          </Button>
        )}
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {renderContent()}
      </Modal.Body>
    </Modal>
  );
};
